import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid } from '@mui/x-data-grid';
import useAuth from '../hooks/useAuth';
import { listAllStudents, listCohorts } from '../services/collegeApi';
import { formatPercent } from '../utils/formatters';
import StatusChip from '../components/common/StatusChip';

const STATUS_FILTERS = [
  { value: 'all', label: 'All Students' },
  { value: 'on_track', label: 'On Track' },
  { value: 'at_risk', label: 'At Risk' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

export default function StudentRosterPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [students, setStudents] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [cohortFilter, setCohortFilter] = useState(searchParams.get('cohort') || 'all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!user?.college_id) return;
    setLoading(true);
    Promise.all([
      listAllStudents(user.college_id).catch(() => ({ students: [] })),
      listCohorts(user.college_id).catch(() => ({ cohorts: [] })),
    ])
      .then(([s, c]) => {
        setStudents(s?.students || []);
        setCohorts(c?.cohorts || []);
      })
      .catch((err) => setError(err?.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [user]);

  const filtered = useMemo(() => {
    return students.filter((s) => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (cohortFilter !== 'all' && s.cohort_id !== cohortFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = [s.name, s.email, s.cohort_name, s.mentor_name].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [students, statusFilter, cohortFilter, search]);

  const columns = [
    {
      field: 'name',
      headerName: 'Student',
      flex: 1.4,
      minWidth: 180,
      renderCell: (p) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {p.row.name || '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {p.row.email}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'cohort_name',
      headerName: 'Cohort',
      flex: 1,
      minWidth: 140,
      renderCell: (p) => p.row.cohort_name || '—',
    },
    {
      field: 'progress_pct',
      headerName: 'Progress',
      flex: 1.2,
      minWidth: 160,
      renderCell: (p) => {
        const pct = p.row.progress_pct
          ?? formatPercent(p.row.assessments_completed, p.row.assessments_required);
        const color = pct >= 100 ? '#0d9488' : pct >= 66 ? '#16a34a' : pct >= 33 ? '#f59e0b' : '#dc2626';
        return (
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color, fontVariantNumeric: 'tabular-nums' }}>
                {pct}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {p.row.assessments_completed ?? 0}/{p.row.assessments_required ?? 0}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, pct)}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: '#e5e7eb',
                '& .MuiLinearProgress-bar': { backgroundColor: color },
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'mentor_name',
      headerName: 'Mentor / TA',
      flex: 1,
      minWidth: 140,
      renderCell: (p) => p.row.mentor_name || <Typography variant="caption" color="text.secondary">Unassigned</Typography>,
    },
    {
      field: 'hours_logged',
      headerName: 'Hours',
      width: 90,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => (
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {p.row.hours_logged ?? 0}/{p.row.hours_required ?? 0}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (p) => <StatusChip status={p.row.status} />,
    },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 0.5 }}>Student Roster</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {filtered.length} of {students.length} students
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField
            placeholder="Search by name, email, mentor…"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <TextField
            select
            size="small"
            label="Cohort"
            value={cohortFilter}
            onChange={(e) => setCohortFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">All cohorts</MenuItem>
            {cohorts.map((c) => (
              <MenuItem key={c.cohort_id} value={c.cohort_id}>{c.name}</MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            {STATUS_FILTERS.map((f) => (
              <Chip
                key={f.value}
                label={f.label}
                clickable
                onClick={() => setStatusFilter(f.value)}
                color={statusFilter === f.value ? 'primary' : 'default'}
                variant={statusFilter === f.value ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ height: 620 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={filtered.map((s) => ({ id: s.student_id || s.enrollment_id, ...s }))}
            columns={columns}
            disableRowSelectionOnClick
            onRowClick={(p) => navigate(`/credits?student=${p.row.student_id || p.id}`)}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[25, 50, 100]}
            density="comfortable"
          />
        )}
      </Paper>
    </Box>
  );
}
